import { createContext, ReactNode, useContext, useReducer } from 'react';
import SnackbarContext from './snackbar-context';
import {
	sendLoginRequest,
	sendLogoutRequest,
	getTeacherRegistrationStatus,
	getStudentRegistrationStatus,
} from '../api/auth-api';
import jwt_decode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { accountPage, panelPage, registrationPage, studentsPage } from './fixed-routing';
import { debugLog, debugError } from '../utils/debug';

type AuthContextType = {
	token: string | null;
	email: string | null;
	id: number | null;
	firstName: string | null;
	lastName: string | null;
	role: string | null;
	districtId: number | null;
	isAdmin: boolean;
	isTeacher: boolean;
	isSecretary: boolean;
	isStudent: boolean;
	login: (login: string, password: string, onError?: (message: string, code?: string) => void) => void;
	/** Logowanie bez przekierowania — np. po rejestracji wznowieniowej na edycję. */
	establishSession: (email: string, password: string) => Promise<boolean>;
	logout: () => void;
	isLoggedIn: () => boolean;
};

type ActionType = {
	type: string;
	value?: any;
};

const LOGIN = 'setLogin';
const LOGOUT = 'setLogout';
const USER = 'userData';
const IMPERSONATION_COOKIE = 'olympics_impersonation_user';

const INITIAL_AUTH_STATE: AuthContextType = {
	token: null,
	email: null,
	id: null,
	firstName: null,
	lastName: null,
	role: null,
	districtId: null,
	isAdmin: false,
	isTeacher: false,
	isSecretary: false,
	isStudent: false,
	login: (login: string, password: string, onError?: (message: string, code?: string) => void) => {},
	establishSession: async () => false,
	logout: () => {},
	isLoggedIn: () => false,
};

const authReducer = (state: AuthContextType, action: ActionType): AuthContextType => {
	switch (action.type) {
		case LOGIN: {
			return {
				...state,
				token: action.value.token,
				email: action.value.email,
				id: action.value.id,
				firstName: action.value.firstName,
				lastName: action.value.lastName,
				role: action.value.role,
				districtId: action.value.districtId,
				isAdmin: action.value.role === 'administrator',
				isSecretary: action.value.role === 'secretary',
				isTeacher: action.value.role === 'teacher',
				isStudent: action.value.role === 'student',
			};
		}
		case LOGOUT: {
			return {
				...state,
				token: null,
				email: null,
				id: null,
				firstName: null,
				lastName: null,
				role: null,
				districtId: null,
				isAdmin: false,
				isSecretary: false,
				isTeacher: false,
				isStudent: false,
			};
		}
		default:
			return state;
	}
};

const getCookie = (name: string): string | null => {
	const matches = document.cookie.match(
		new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
	);
	return matches ? decodeURIComponent(matches[1]) : null;
};

const normalizeImpersonationCookieJson = (cookieValue: string): string => {
	// If value still looks URL-encoded after one decode (e.g. starts with "%7B"),
	// attempt one extra decode for backward compatibility with older backend encoding.
	if (cookieValue.includes('%7B') || cookieValue.includes('%22')) {
		try {
			return decodeURIComponent(cookieValue);
		} catch (error) {
			debugError('Auth context (impersonation extra decode) error:', error);
		}
	}
	return cookieValue;
};

const clearCookie = (name: string) => {
	// Expire cookie for root path; domain is left default intentionally
	document.cookie = `${name}=; Max-Age=-1; path=/`;
};

const getInitialState = () => {
	// 1) Try to bootstrap from one-time impersonation cookie (set by WP plugin)
	try {
		const impersonationJson = getCookie(IMPERSONATION_COOKIE);
		if (impersonationJson) {
			debugLog('Auth context: impersonation cookie found', { length: impersonationJson.length });
			clearCookie(IMPERSONATION_COOKIE);
			const normalizedImpersonationJson = normalizeImpersonationCookieJson(impersonationJson);
			const user = JSON.parse(normalizedImpersonationJson);

			try {
				const tokenData = jwt_decode(user.token) as any;

				// Persist to localStorage so subsequent reloads work like normal login
				localStorage.setItem(USER, JSON.stringify(user));

				return {
					...INITIAL_AUTH_STATE,
					token: user.token,
					email: user.email,
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					role: user.role,
					districtId: tokenData.districtId,
					isAdmin: user.role === 'administrator',
					isSecretary: user.role === 'secretary',
					isTeacher: user.role === 'teacher',
					isStudent: user.role === 'student',
				};
			} catch (error) {
				debugError('Auth context (impersonation) error:', error);
				// Fall through to normal localStorage flow
			}
		}
	} catch (error) {
		// If anything goes wrong with cookie parsing, log and continue with normal auth
		debugError('Auth context (impersonation cookie read) error:', error);
	}

	// 2) Fallback to standard localStorage-based session (normal login)
	const userJson = localStorage.getItem(USER);
	if (userJson) {
		const user = JSON.parse(userJson);
		try {
			const tokenData = jwt_decode(user.token) as any;

			return {
				...INITIAL_AUTH_STATE,
				token: user.token,
				email: user.email,
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				role: user.role,
				districtId: tokenData.districtId,
				isAdmin: user.role === 'administrator',
				isSecretary: user.role === 'secretary',
				isTeacher: user.role === 'teacher',
				isStudent: user.role === 'student',
			};
		} catch (error) {
			debugError('Auth context error:', error);
		}
	}
	return INITIAL_AUTH_STATE;
};

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
	const [authState, dispatch] = useReducer(authReducer, getInitialState());
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();

	const isLoggedIn = () => {
		if (authState.token) {
			try {
				const userData = jwt_decode(authState.token) as any;
				if (userData.exp > Math.round(Date.now() / 1000)) {
					return true;
				} else {
					// Token wygasł - wyczyść automatycznie
					debugLog('Token expired, clearing localStorage');
					localStorage.removeItem(USER);
					dispatch({ type: LOGOUT });
				}
			} catch (error) {
				debugLog('Invalid token, clearing localStorage:', error);
				localStorage.removeItem(USER);
				dispatch({ type: LOGOUT });
			}
		}
		return false;
	};

	const login = (email: string, password: string, onError?: (message: string, code?: string) => void) => {
		// Try to logout any existing session first, but don't block login if it fails
		// (e.g., if there's no active session, logout may return 500, but that's OK)
		sendLogoutRequest()
			.catch((error) => {
				// Logout failed (e.g., no session exists) - that's fine, continue with login
				debugLog('Logout before login failed (likely no active session):', error);
			})
			.finally(() => {
				// Always proceed with login, regardless of logout result
				sendLoginRequest(email, password, (errorMessage, code) => {
					setMsg({
						msg: errorMessage || 'Niepoprawny login lub hasło',
						severity: 'error',
					});
					onError?.(errorMessage || 'Niepoprawny login lub hasło', code);
				})
					.then(async (data: any) => {
						if (data) {
							const tokenData = jwt_decode(data.token) as any;
							debugLog('Login successful, tokenData:', tokenData);
							const user = {
								token: data.token,
								firstName: data.firstName,
								lastName: data.lastName,
								email: data.email,
								id: data.id,
								role: tokenData.role,
							};
							localStorage.setItem(USER, JSON.stringify(user));
							dispatch({ type: LOGIN, value: { ...data, role: tokenData.role, districtId: tokenData.districtId } });
							debugLog('User role:', tokenData.role);
							if (tokenData.role === 'teacher') {
								try {
									const status = await getTeacherRegistrationStatus(data.token);
									if (!status?.hasProfileOnLatest) {
										debugLog('Redirecting to teacher registration resume');
										navigate(registrationPage + '?resume=teacher');
										return;
									}
								} catch (error) {
									debugError('Teacher registration status check failed:', error);
								}
							}
							if (tokenData.role === 'student') {
								try {
									const status = await getStudentRegistrationStatus(data.token);
									if (!status?.hasProfileOnLatest) {
										debugLog('Redirecting to student registration resume');
										navigate(registrationPage + '?resume=student', {
											state: { studentPrefill: status?.prefill ?? null },
										});
										return;
									}
								} catch (error) {
									debugError('Student registration status check failed:', error);
								}
								debugLog('Redirecting to account:', accountPage);
								navigate(accountPage);
								return;
							}
							if (
								tokenData.role === 'administrator' ||
								tokenData.role === 'teacher' ||
								tokenData.role === 'secretary'
							) {
								const target = tokenData.role === 'teacher' ? studentsPage : panelPage;
								debugLog('Redirecting to panel:', target);
								navigate(target);
							}
						}
					})
					.catch((error: any) => {
						setMsg({
							msg: 'Wystąpił błąd podczas logowania. Spróbuj ponownie później! ',
						});
						debugError('Auth context error:', error);
					});
			});
	};

	const establishSession = async (email: string, password: string): Promise<boolean> => {
		try {
			const data = await sendLoginRequest(email, password);
			if (!data?.token) {
				return false;
			}
			const tokenData = jwt_decode(data.token) as { role?: string; districtId?: number };
			const user = {
				token: data.token,
				firstName: data.firstName,
				lastName: data.lastName,
				email: data.email,
				id: data.id,
				role: tokenData.role,
			};
			localStorage.setItem(USER, JSON.stringify(user));
			dispatch({
				type: LOGIN,
				value: { ...data, role: tokenData.role, districtId: tokenData.districtId },
			});
			return true;
		} catch (error) {
			debugError('establishSession failed:', error);
			return false;
		}
	};

	const logout = () => {
		sendLogoutRequest();
		localStorage.clear(); // Clear all localStorage, not just USER
		sessionStorage.clear(); // Also clear per-session panel filters/tabs
		dispatch({ type: LOGOUT });
	};

	const contextValue: AuthContextType = {
		...authState,
		login,
		establishSession,
		logout,
		isLoggedIn,
	};
	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const AuthContext = createContext<AuthContextType>({
	...INITIAL_AUTH_STATE,
});

export default AuthContext;
